import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useWebSocket, WebSocketProvider } from '../context/WebSocketContext';
import RoomGameContainer from '../components/RoomGameContainer';

import { ChampionConfig } from '../../StaticSandbox/types/staticTypes';
import './DamageCalculator.css';

interface DamageCalculatorProps {
  championName?: string; // Optional champion name
}

// Internal component that uses WebSocket context
const DamageCalculatorInternal: React.FC<{ championName?: string }> = ({ championName }) => {
  const [error, setError] = useState<string | null>(null);
  const [initializationSent, setInitializationSent] = useState(false);
  const [championConfigs, setChampionConfigs] = useState<{
    mainChampion?: ChampionConfig;
    targetChampion?: ChampionConfig;
  }>({});
  const initializedChampionRef = useRef<string | undefined>(undefined);
  
  // WebSocket connection for damage calculator functionality
  const { socket, roomId, status } = useWebSocket();
  
  // Get URL parameters and navigation state
  const location = useLocation();
  const navigationState = location.state as {
    mainChampion?: ChampionConfig;
    targetChampion?: ChampionConfig;
    fromStaticTracker?: boolean;
  } | null;
  
  // Parse champion configurations from navigation state, sessionStorage, or URL parameters
  useEffect(() => {
    console.log('[DamageCalculator] 🔍 Parsing champion configurations...');
    let mainChampion: ChampionConfig | undefined;
    let targetChampion: ChampionConfig | undefined;
    
    // First priority: navigation state (prop drilling approach)
    if (navigationState?.mainChampion && navigationState?.targetChampion) {
      console.log('[DamageCalculator] ✅ Using champion configs from navigation state:', navigationState);
      mainChampion = navigationState.mainChampion;
      targetChampion = navigationState.targetChampion;
    } else {
      // Second priority: sessionStorage (for new tab approach)
      console.log('[DamageCalculator] 🔍 Checking sessionStorage for export data...');
      const storedData = sessionStorage.getItem('damageCalculatorExport');
      console.log('[DamageCalculator] 📦 Raw sessionStorage data:', storedData);
      
      if (storedData) {
        try {
          const exportData = JSON.parse(storedData);
          console.log('[DamageCalculator] 📋 Parsed export data:', exportData);
          
          if (exportData.mainChampion && exportData.targetChampion) {
            console.log('[DamageCalculator] ✅ Using champion configs from sessionStorage:', {
              mainChampion: exportData.mainChampion,
              targetChampion: exportData.targetChampion
            });
            mainChampion = exportData.mainChampion;
            targetChampion = exportData.targetChampion;
            // Clear the data after use
            sessionStorage.removeItem('damageCalculatorExport');
          } else {
            console.warn('[DamageCalculator] ⚠️ Export data missing mainChampion or targetChampion:', exportData);
          }
        } catch (error) {
          console.error('[DamageCalculator] ❌ Error parsing sessionStorage data:', error);
        }
      } else {
        console.log('[DamageCalculator] ❌ No sessionStorage data found, checking localStorage...');
        
        // Fallback to localStorage
        const localStoredData = localStorage.getItem('damageCalculatorExport');
        console.log('[DamageCalculator] 📦 Raw localStorage data:', localStoredData);
        
        if (localStoredData) {
          try {
            const exportData = JSON.parse(localStoredData);
            console.log('[DamageCalculator] 📋 Parsed localStorage export data:', exportData);
            
            if (exportData.mainChampion && exportData.targetChampion) {
              console.log('[DamageCalculator] ✅ Using champion configs from localStorage:', {
                mainChampion: exportData.mainChampion,
                targetChampion: exportData.targetChampion
              });
              mainChampion = exportData.mainChampion;
              targetChampion = exportData.targetChampion;
              // Clear the data after use
              localStorage.removeItem('damageCalculatorExport');
            } else {
              console.warn('[DamageCalculator] ⚠️ localStorage export data missing mainChampion or targetChampion:', exportData);
            }
          } catch (error) {
            console.error('[DamageCalculator] ❌ Error parsing localStorage data:', error);
          }
        } else {
          console.log('[DamageCalculator] ❌ No localStorage data found either');
        }
      }
      
      // Third priority: URL parameters (fallback approach)
      if (!mainChampion || !targetChampion) {
        const urlParams = new URLSearchParams(location.search);
        const mainChampionParam = urlParams.get('mainChampion');
        const targetChampionParam = urlParams.get('targetChampion');
        
        if (mainChampionParam && targetChampionParam) {
          try {
            mainChampion = JSON.parse(mainChampionParam);
            targetChampion = JSON.parse(targetChampionParam);
            console.log('[DamageCalculator] Using champion configs from URL parameters:', {
              mainChampion,
              targetChampion
            });
          } catch (error) {
            console.error('[DamageCalculator] Error parsing champion configs from URL:', error);
            setError('Invalid champion configuration in URL');
            return;
          }
        }
      }
    }
    
    if (mainChampion && targetChampion) {
      console.log('[DamageCalculator] 🎯 Setting champion configs:', { mainChampion, targetChampion });
      setChampionConfigs({
        mainChampion,
        targetChampion
      });
      console.log('[DamageCalculator] ✅ Champion configs set successfully');
    } else {
      console.log('[DamageCalculator] ❌ Missing champion configs:', { 
        hasMainChampion: !!mainChampion, 
        hasTargetChampion: !!targetChampion 
      });
    }
  }, [location.search, navigationState]);

  // Initialize the socket connection for damage calculator mode
  useEffect(() => {
    console.log(`[DamageCalculator] 🚀 INIT EFFECT TRIGGERED - Socket: ${!!socket}, ReadyState: ${socket?.readyState}, Room: ${roomId}, Status: ${status}`);
    
    if (socket && socket.readyState === WebSocket.OPEN && roomId && status === 'connected') {
      // For damage calculator mode, we can initialize even without a specific championName
      // Check if we've already initialized for this champion (or for general damage calculator mode)
      const initKey = championName || 'damage-calculator-mode';
      if (initializedChampionRef.current === initKey) {
        console.log(`[DamageCalculator] ⏭️ Already initialized for ${initKey}, skipping`);
        return;
      }
      
      console.log(`[DamageCalculator] ✅ INITIALIZING DAMAGE CALCULATOR MODE${championName ? ` for ${championName}` : ''} in room ${roomId}`);
      
      try {
        // Initialize damage calculator mode - use dedicated damage calculator message
        const message = {
          type: 'init-damage-calculator',
          data: { championName: championName || null }
        };
        
        console.log(`[DamageCalculator] 📤 SENDING INIT MESSAGE:`, message);
        socket.send(JSON.stringify(message));
        
        setInitializationSent(true);
        initializedChampionRef.current = initKey;
        setError(null);
        console.log(`[DamageCalculator] ✅ Damage calculator initialization message sent successfully`);
      } catch (error) {
        console.error(`[DamageCalculator] ❌ Error sending initialization message:`, error);
        setError(`Failed to initialize damage calculator: ${error}`);
      }
    } else {
      console.log(`[DamageCalculator] ⏸️ NOT READY TO INITIALIZE:`);
      console.log(`  - Socket exists: ${!!socket}`);
      console.log(`  - Socket readyState: ${socket?.readyState} (OPEN=${WebSocket.OPEN})`);
      console.log(`  - Room ID: ${roomId}`);
      console.log(`  - Status: ${status}`);
      setInitializationSent(false);
      initializedChampionRef.current = undefined;
    }
  }, [socket, roomId, status, championName]); // Re-add championName but with proper tracking

  // Add champions to the damage calculator when socket is ready and we have configs
  useEffect(() => {
    console.log('[DamageCalculator] 🏆 Champion adding effect triggered:', {
      hasSocket: !!socket,
      socketReady: socket?.readyState === WebSocket.OPEN,
      status,
      initializationSent,
      hasMainChampion: !!championConfigs.mainChampion,
      hasTargetChampion: !!championConfigs.targetChampion,
      championConfigs
    });
    
    if (socket && socket.readyState === WebSocket.OPEN && 
        status === 'connected' && initializationSent && 
        championConfigs.mainChampion && championConfigs.targetChampion) {
      
      console.log('[DamageCalculator] ✅ All conditions met, adding champions from configs:', championConfigs);
      
      // Helper function to convert ChampionConfig runes to backend RuneStats format
      const convertRunesToBackendFormat = (runes: ChampionConfig['runes']) => {
        if (!runes) return null;
        
        return {
          primary_style_id: parseInt(runes.primaryPath),
          primary_perk_ids: runes.primaryRunes.join(','),
          secondary_style_id: parseInt(runes.secondaryPath),
          secondary_perk_ids: runes.secondaryRunes.join(','),
          shard_offense_id: runes.shards[0] ? parseInt(runes.shards[0]) : null,
          shard_flex_id: runes.shards[1] ? parseInt(runes.shards[1]) : null,
          shard_defense_id: runes.shards[2] ? parseInt(runes.shards[2]) : null
        };
      };
      
      // Add main champion at position (0, 0)
      const addMainChampion = () => {
        const mainConfig = championConfigs.mainChampion!;
        const championData = {
          name: mainConfig.name,
          team: 'blue',
          level: mainConfig.level,
          position: { x: 0, y: 0 },
          items: mainConfig.items,
          runes: convertRunesToBackendFormat(mainConfig.runes),
          skillOrder: mainConfig.skillOrder
        };
        
        console.log('[DamageCalculator] Adding main champion:', championData);
        
        socket.send(JSON.stringify({
          type: 'game-message',
          data: {
            type: 'add-champion',
            data: championData
          }
        }));
      };
      
      // Add target champion at position (1, 0)
      const addTargetChampion = () => {
        const targetConfig = championConfigs.targetChampion!;
        const championData = {
          name: targetConfig.name,
          team: 'red',
          level: targetConfig.level,
          position: { x: 1, y: 0 },
          items: targetConfig.items,
          runes: convertRunesToBackendFormat(targetConfig.runes),
          skillOrder: targetConfig.skillOrder
        };
        
        console.log('[DamageCalculator] Adding target champion:', championData);
        
        socket.send(JSON.stringify({
          type: 'game-message',
          data: {
            type: 'add-champion',
            data: championData
          }
        }));
      };
      
      // Add champions with a slight delay to ensure proper initialization
      setTimeout(() => {
        addMainChampion();
        setTimeout(() => {
          addTargetChampion();
        }, 100);
      }, 500);
    }
  }, [socket, status, initializationSent, championConfigs]);

  // Show error state if there's an error
  if (error) {
    return (
      <div className="damage-calculator-error">
        <div className="error-container">
          <h3>Damage Calculator Error</h3>
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="damage-calculator">
      {/* Connection Status Info */}
    {/*   <div className="damage-calculator-info">
        <h2>{getHeaderText()}</h2>
        <div className="connection-status">
          <div className={`status-indicator ${status === 'connected' ? 'connected' : 'disconnected'}`}></div>
          <span>Room: {roomId}</span>
          <button onClick={testConnection} className="test-connection-btn">
            Test Connection
          </button>
        </div>
      </div> */}

      {/* Main Content - RoomGameContainer */}
      <div className="damage-calculator-content">
        <RoomGameContainer championName={championName} />
      </div>
    </div>
  );
};

// Main component that provides WebSocket context
const DamageCalculator: React.FC<DamageCalculatorProps> = ({ championName: propChampionName }) => {
  const { name } = useParams<{ name?: string }>();
  const championName = propChampionName || name; // Use prop first, then URL param

  return (
    <WebSocketProvider>
      <Helmet>
        <title>Damage Calculator Sandbox – LoL Calculator</title>
        <meta name="description" content="Free League of Legends damage calculator sandbox. Add champions, customize items and runes, and simulate real-time combat damage in LoL." />
        <link rel="canonical" href="https://lolcalculator.gg/damage-calculator" />
      </Helmet>
      <DamageCalculatorInternal championName={championName} />
    </WebSocketProvider>
  );
};

export default DamageCalculator; 